import {
    Chip,
    Select,
    MenuItem,
    FormControl,
    Typography,
    Button,
    TextField
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useTheme, makeStyles } from "@material-ui/core/styles";
import React, { useEffect, useState, useMemo } from "react";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import toastError from "../../errors/toastError";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import { getKanbanLaneOrder } from "../../services/companyKanbanService";
import { i18n } from "../../translate/i18n";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import ConnectionIcon from "../ConnectionIcon";

const useStyles = makeStyles((theme) => ({
    ...getStandardButtonStyles(theme),
    customSelect: {
        borderRadius: "8px !important",
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "8px !important",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: `${theme.palette.primary.main} !important`,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: `${theme.palette.primary.main} !important`,
        },
        "& .MuiSelect-select": {
            padding: "10px 14px",
            fontSize: "0.85rem",
        },
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px !important",
            padding: "4px 8px !important",
        },
        "& .MuiOutlinedInput-input": {
            padding: "4px 6px !important",
            fontSize: "0.82rem !important",
        }
    },
    labelCaption: {
        fontSize: "0.78rem",
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: 4,
        display: "block",
    }
}));

export function TagsKanbanContainer({ ticket }) {
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();

    const [ticketConnection, setTicketConnection] = useState(null);
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState("");
    const [boards, setBoards] = useState([]);
    const [selectedBoard, setSelectedBoard] = useState("");
    const [tags, setTags] = useState([]);
    const [selected, setSelected] = useState("");
    const [laneOrder, setLaneOrder] = useState(null);
    const [ticketTags, setTicketTags] = useState(ticket?.tags || []);

    useEffect(() => {
        if (ticket?.tags) {
            setTicketTags(ticket.tags);
        }
    }, [ticket?.tags]);

    // Etapa actualmente seleccionada
    const currentLane = useMemo(() => {
        if (!selected || !tags) return null;
        return tags.find(t => Number(t.id) === Number(selected)) || null;
    }, [selected, tags]);

    // Sub-etiquetas de Kanban disponibles para la etapa seleccionada
    const availableLaneTags = useMemo(() => {
        if (!currentLane || !Array.isArray(currentLane.kanbanLaneTags)) return [];
        return currentLane.kanbanLaneTags
            .map(klt => klt.tag)
            .filter(t => t && t.id);
    }, [currentLane]);

    // Etiquetas de Kanban actualmente asignadas al ticket (excluyendo la etapa kanban: 1)
    const selectedKanbanTags = useMemo(() => {
        if (!Array.isArray(ticketTags)) return [];
        return ticketTags.filter(t => 
            t && 
            Number(t.id) !== Number(selected) && 
            Number(t.kanban) !== 1 &&
            String(t.kanban) !== "1"
        );
    }, [ticketTags, selected]);

    // Opciones completas para Autocomplete (incluye las asignadas para evitar warnings)
    const autocompleteOptions = useMemo(() => {
        const options = [...availableLaneTags];
        selectedKanbanTags.forEach(st => {
            if (!options.some(opt => Number(opt.id) === Number(st.id))) {
                options.push(st);
            }
        });
        return options;
    }, [availableLaneTags, selectedKanbanTags]);

    // Cargar conexión, marcas y filtrar estrictamente según la conexión del ticket
    useEffect(() => {
        let isMounted = true;
        const fetchBrandsAndDetect = async () => {
            try {
                // 1. Cargar catálogo de marcas
                const { data: brandsData } = await api.get("/brands");
                if (!isMounted) return;
                const brandList = Array.isArray(brandsData) ? brandsData : (brandsData?.brands || []);

                // 2. Determinar la conexión y la marca vinculada a la conexión/canal del ticket
                const wpId = ticket?.whatsappId || ticket?.whatsapp?.id;
                let brandIdToSelect = ticket?.whatsapp?.brandId;
                let resolvedConn = ticket?.whatsapp || null;

                if (wpId) {
                    try {
                        const { data: whatsapps } = await api.get("/whatsapp");
                        const currentWp = whatsapps?.find(w => Number(w.id) === Number(wpId));
                        if (currentWp) {
                            resolvedConn = currentWp;
                            if (currentWp.brandId) {
                                brandIdToSelect = currentWp.brandId;
                            }
                        }
                    } catch (e) {
                        console.error("[TagsKanbanContainer] Error fetching whatsapp connection:", e);
                    }
                }

                // 3. Filtrado estricto: mostrar únicamente la marca vinculada a este número/canal
                let filteredBrandList = brandList;
                if (brandIdToSelect) {
                    const matchedBrands = brandList.filter(b => Number(b.id) === Number(brandIdToSelect));
                    if (matchedBrands.length > 0) {
                        filteredBrandList = matchedBrands;
                    }
                }

                if (isMounted) {
                    setTicketConnection(resolvedConn);
                    setBrands(filteredBrandList);
                    if (brandIdToSelect) {
                        setSelectedBrand(Number(brandIdToSelect));
                    } else if (filteredBrandList.length === 1) {
                        setSelectedBrand(Number(filteredBrandList[0].id));
                    }
                }
            } catch (err) {
                console.error("[TagsKanbanContainer] Error loading brands:", err);
            }
        };

        fetchBrandsAndDetect();

        return () => {
            isMounted = false;
        };
    }, [ticket?.id, ticket?.whatsappId, ticket?.whatsapp?.id, ticket?.whatsapp?.brandId]);

    // Cargar embudos (kanban boards) filtrados exclusivamente por la marca y la conexión activa del ticket
    useEffect(() => {
        let isMounted = true;
        const loadBoardsForBrand = async () => {
            if (!selectedBrand) {
                setBoards([]);
                return;
            }
            try {
                const activeWpId = ticketConnection?.id || ticket?.whatsappId || ticket?.whatsapp?.id;
                const queryParams = { brandId: selectedBrand };
                if (activeWpId) {
                    queryParams["whatsappIds[]"] = [activeWpId];
                }

                const { data } = await api.get("/kanban-boards", {
                    params: queryParams
                });
                if (isMounted) {
                    const rawList = Array.isArray(data) ? data : [];

                    // Filtrado estricto en cliente (defensa en profundidad):
                    // El embudo DEBE tener la conexión activa del ticket entre sus whatsapps asignados
                    const boardList = activeWpId
                        ? rawList.filter(board => {
                            if (Array.isArray(board.whatsapps) && board.whatsapps.length > 0) {
                                return board.whatsapps.some(w => Number(w.id) === Number(activeWpId));
                            }
                            if (board.whatsappId) {
                                return Number(board.whatsappId) === Number(activeWpId);
                            }
                            // Si el embudo no tiene ninguna conexión restringida, pertenece a toda la marca
                            return true;
                        })
                        : rawList;

                    setBoards(boardList);

                    // Si el ticket ya tiene kanbanBoardId y pertenece a los embudos filtrados, asignarlo
                    if (ticket?.kanbanBoardId && boardList.some(b => Number(b.id) === Number(ticket.kanbanBoardId))) {
                        setSelectedBoard(ticket.kanbanBoardId);
                    } else if (selectedBoard && !boardList.some(b => Number(b.id) === Number(selectedBoard))) {
                        setSelectedBoard("");
                        setSelected("");
                    }
                }
            } catch (err) {
                toastError(err);
            }
        };

        loadBoardsForBrand();

        return () => {
            isMounted = false;
        };
    }, [selectedBrand, ticket?.kanbanBoardId, ticketConnection?.id, ticket?.whatsappId, ticket?.whatsapp?.id]);

    // Cargar tags kanban y detectar etapa asignada al ticket
    useEffect(() => {
        let isMounted = true;
        const loadAllKanbanTags = async () => {
            try {
                const { data } = await api.get(`/tags/list`, { params: { kanban: 1 } });
                if (!isMounted) return;
                const loadedTags = data || [];
                setTags(loadedTags);

                if (ticket?.tags && ticket.tags.length > 0) {
                    if (ticket.kanbanStageId) {
                        setSelected(ticket.kanbanStageId);
                    } else {
                        for (const t of ticket.tags) {
                            const tagInfo = loadedTags.find(lt => Number(lt.id) === Number(t.id));
                            if (tagInfo && tagInfo.kanbanBoardId) {
                                setSelected(tagInfo.id);
                                if (!selectedBoard) {
                                    setSelectedBoard(tagInfo.kanbanBoardId);
                                }
                                break;
                            }
                        }
                    }
                } else {
                    setSelected("");
                }
            } catch (err) {
                console.error("[TagsKanbanContainer] Error loading tags:", err);
            }
        };

        loadAllKanbanTags();

        return () => {
            isMounted = false;
        };
    }, [ticket?.tags, ticket?.kanbanStageId]);

    // Cargar orden de carriles (lane order) del tablero activo
    useEffect(() => {
        if (selectedBoard) {
            getKanbanLaneOrder(selectedBoard).then(order => {
                if (order) setLaneOrder(order);
                else setLaneOrder(null);
            }).catch(() => setLaneOrder(null));
        } else {
            setLaneOrder(null);
        }
    }, [selectedBoard]);

    // Filtrar etapas que corresponden al tablero seleccionado
    const filteredTags = selectedBoard
        ? tags.filter(tag => Number(tag.kanbanBoardId) === Number(selectedBoard) || (selectedBoard === 0 && !tag.kanbanBoardId))
        : [];

    if (laneOrder && Array.isArray(laneOrder)) {
        filteredTags.sort((a, b) => {
            const indexA = laneOrder.indexOf(String(a.id));
            const indexB = laneOrder.indexOf(String(b.id));
            if (indexA === -1 && indexB === -1) return 0;
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;
            return indexA - indexB;
        });
    }

    const handleBrandChange = (e) => {
        const brandId = e.target.value;
        setSelectedBrand(brandId);
        setSelectedBoard("");
        setSelected("");
    };

    const handleBoardChange = (e) => {
        const boardId = e.target.value;
        setSelectedBoard(boardId);
        setSelected("");
    };

    const handleStageChange = async (e) => {
        const value = e.target.value;
        setSelected(value);

        try {
            if (ticket.tags && ticket.tags.length > 0) {
                await api.delete(`/ticket-tags/${ticket.id}`);
            }
            if (value && value !== "" && value !== null) {
                await api.post(`/ticket-tags/${ticket.id}/${value}`, {}, {
                    params: { origin: 'inbox' }
                });
                const newLaneTag = tags.find(t => Number(t.id) === Number(value));
                setTicketTags(prev => {
                    const withoutOldLanes = prev.filter(t => Number(t.kanban) !== 1 && String(t.kanban) !== "1");
                    return newLaneTag ? [...withoutOldLanes, newLaneTag] : withoutOldLanes;
                });
            } else {
                setTicketTags(prev => prev.filter(t => Number(t.kanban) !== 1 && String(t.kanban) !== "1"));
            }
        } catch (err) {
            toastError(err);
            if (ticket.tags && ticket.tags.length > 0) {
                setSelected(ticket.tags[0].id);
            } else {
                setSelected("");
            }
        }
    };

    const handleKanbanTagsChange = async (event, newValues, reason, details) => {
        if (reason === 'select-option' && details?.option) {
            const tagToAdd = details.option;
            setTicketTags(prev => [...prev, tagToAdd]);
            try {
                await api.post(`/ticket-tags/${ticket.id}/${tagToAdd.id}`, {}, {
                    params: { origin: 'inbox' }
                });
            } catch (err) {
                toastError(err);
                setTicketTags(prev => prev.filter(t => Number(t.id) !== Number(tagToAdd.id)));
            }
        } else if (reason === 'remove-option') {
            const tagToRemove = details?.option || selectedKanbanTags.find(t => !newValues.some(nv => Number(nv.id) === Number(t.id)));
            if (tagToRemove) {
                setTicketTags(prev => prev.filter(t => Number(t.id) !== Number(tagToRemove.id)));
                try {
                    await api.delete(`/ticket-tags/${ticket.id}/${tagToRemove.id}`);
                } catch (err) {
                    toastError(err);
                    setTicketTags(prev => [...prev, tagToRemove]);
                }
            }
        } else if (reason === 'clear') {
            const toDelete = [...selectedKanbanTags];
            setTicketTags(prev => prev.filter(t => Number(t.id) === Number(selected)));
            try {
                for (const tag of toDelete) {
                    await api.delete(`/ticket-tags/${ticket.id}/${tag.id}`);
                }
            } catch (err) {
                toastError(err);
            }
        }
    };

    // Ocultar si el ticket es un grupo
    if (ticket && (ticket.isGroup || (ticket.contact && ticket.contact.isGroup))) {
        return null;
    }

    return (
        <div style={{ width: "100%", padding: "4px 2px" }}>
            {/* 0. Conexión del Ticket (Inmutable / Bloqueado) */}
            <div style={{ marginBottom: 10 }}>
                <span className={classes.labelCaption}>
                    {i18n.t("tags.kanban.connection")}
                </span>
                <FormControl fullWidth size="small" variant="outlined">
                    <Select
                        value={ticketConnection?.id || (ticket?.whatsappId || "")}
                        displayEmpty
                        variant="outlined"
                        className={classes.customSelect}
                        disabled={true}
                        renderValue={() => {
                            const connObj = ticketConnection || ticket?.whatsapp;
                            if (!connObj && !ticket?.whatsappId) {
                                return <span style={{ color: '#aaa' }}>{i18n.t("tags.kanban.noConnection")}</span>;
                            }
                            const channelType = connObj?.channel || 'whatsapp';
                            return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                                    <ConnectionIcon connectionType={channelType} />
                                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {connObj?.name || `Conexión #${ticket?.whatsappId || connObj?.id}`}
                                    </span>
                                </div>
                            );
                        }}
                    >
                        <MenuItem value={ticketConnection?.id || (ticket?.whatsappId || "")}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <ConnectionIcon connectionType={ticketConnection?.channel || ticket?.whatsapp?.channel || 'whatsapp'} />
                                <span>{ticketConnection?.name || ticket?.whatsapp?.name || `Conexión #${ticketConnection?.id || ticket?.whatsappId}`}</span>
                            </div>
                        </MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* 1. Selector de Marca (estrictamente filtrado por conexión) */}
            <div style={{ marginBottom: 10 }}>
                <span className={classes.labelCaption}>
                    {i18n.t("tags.kanban.selectBrand")}
                </span>
                <FormControl fullWidth size="small" variant="outlined">
                    <Select
                        value={selectedBrand || ""}
                        onChange={handleBrandChange}
                        displayEmpty
                        variant="outlined"
                        className={classes.customSelect}
                        disabled={brands.length <= 1}
                        renderValue={(val) => {
                            if (!val) {
                                return <span style={{ color: '#aaa' }}>{i18n.t("tags.kanban.selectBrand")}</span>;
                            }
                            const brand = brands.find(b => Number(b.id) === Number(val));
                            return brand ? brand.name : "";
                        }}
                    >
                        {brands.length > 1 ? (
                            <MenuItem value="" disabled>
                                {i18n.t("tags.kanban.selectBrand")}
                            </MenuItem>
                        ) : null}
                        {brands.map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                                {b.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>

            {/* 2. Selector de Embudo (filtrado exclusivamente por la Marca del canal) */}
            <div style={{ marginBottom: 10 }}>
                <span className={classes.labelCaption}>
                    {i18n.t("tags.kanban.selectBoard")}
                </span>
                <FormControl fullWidth size="small" variant="outlined">
                    <Select
                        value={selectedBoard || ""}
                        onChange={handleBoardChange}
                        displayEmpty
                        variant="outlined"
                        className={classes.customSelect}
                        disabled={!selectedBrand}
                        renderValue={(val) => {
                            if (!val) {
                                return (
                                    <span style={{ color: '#aaa' }}>
                                        {selectedBrand ? i18n.t("tags.kanban.selectBoard") : i18n.t("tags.kanban.selectBrandFirst")}
                                    </span>
                                );
                            }
                            const board = boards.find(b => Number(b.id) === Number(val));
                            return board ? board.name : "";
                        }}
                    >
                        <MenuItem value="" disabled>
                            {selectedBrand ? i18n.t("tags.kanban.selectBoard") : i18n.t("tags.kanban.selectBrandFirst")}
                        </MenuItem>
                        {boards.map((board) => (
                            <MenuItem key={board.id} value={board.id}>
                                {board.name}
                            </MenuItem>
                        ))}
                        {boards.length === 0 && Boolean(selectedBrand) ? (
                            <MenuItem value="" disabled>
                                {i18n.t("tags.kanban.noBoards")}
                            </MenuItem>
                        ) : null}
                    </Select>
                </FormControl>
            </div>

            {/* 3. Selector de Etapa Kanban */}
            <div style={{ marginBottom: 10 }}>
                <span className={classes.labelCaption}>
                    {i18n.t("tags.kanban.selectStage")}
                </span>
                <FormControl fullWidth size="small" variant="outlined">
                    <Select
                        value={selected || ""}
                        onChange={handleStageChange}
                        displayEmpty
                        variant="outlined"
                        className={classes.customSelect}
                        disabled={!selectedBoard}
                        renderValue={() => {
                            if (!selected) {
                                return (
                                    <span style={{ color: '#aaa' }}>
                                        {selectedBoard ? i18n.t("tags.kanban.selectStage") : i18n.t("tags.kanban.noStages")}
                                    </span>
                                );
                            }
                            const tag = tags.find(t => Number(t.id) === Number(selected));
                            if (!tag) return "";
                            return (
                                <Chip
                                    label={tag.name}
                                    style={{
                                        backgroundColor: tag.color,
                                        color: "#FFF",
                                        height: 22,
                                        fontSize: "0.72rem",
                                        fontWeight: 500,
                                        letterSpacing: '0.3px',
                                    }}
                                />
                            );
                        }}
                    >
                        <MenuItem value="" disabled>
                            {i18n.t("tags.kanban.selectStage")}
                        </MenuItem>
                        {filteredTags.map((tag) => (
                            <MenuItem key={tag.id} value={tag.id}>
                                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                    <span
                                        style={{
                                            backgroundColor: tag.color,
                                            width: 14,
                                            height: 14,
                                            borderRadius: "50%",
                                            marginRight: 8
                                        }}
                                    />
                                    {tag.name}
                                </div>
                            </MenuItem>
                        ))}
                        {filteredTags.length === 0 ? (
                            <MenuItem value="" disabled>
                                {i18n.t("tags.kanban.noStages")}
                            </MenuItem>
                        ) : null}
                    </Select>
                </FormControl>
            </div>

            {/* 4. Selector de Etiquetas de Kanban (Sub-etiquetas de la Etapa) */}
            <div style={{ marginBottom: 12 }}>
                <span className={classes.labelCaption}>
                    {i18n.t("tags.kanban.subtagsTitle")}
                </span>
                <Autocomplete
                    multiple
                    size="small"
                    options={autocompleteOptions}
                    value={selectedKanbanTags}
                    onChange={handleKanbanTagsChange}
                    getOptionLabel={(option) => option?.name || ""}
                    getOptionSelected={(option, value) => Number(option?.id) === Number(value?.id)}
                    disabled={!selected}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                {...getTagProps({ index })}
                                key={option.id || index}
                                label={option.name}
                                size="small"
                                style={{
                                    backgroundColor: option.color || theme.palette.primary.main,
                                    color: "#FFF",
                                    height: 22,
                                    fontSize: "0.72rem",
                                    fontWeight: 500,
                                    letterSpacing: '0.3px',
                                    marginRight: 4,
                                    marginBottom: 4
                                }}
                            />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            className={classes.customSelect}
                            placeholder={
                                !selected
                                    ? i18n.t("tags.kanban.selectStageFirst")
                                    : availableLaneTags.length === 0
                                    ? i18n.t("tags.kanban.noSubtags")
                                    : i18n.t("tags.kanban.subtagsPlaceholder")
                            }
                        />
                    )}
                />
            </div>

            {/* 5. Botón Ir a Kanban */}
            {selectedBoard && (
                <Button
                    fullWidth
                    className={classes.applyButton}
                    startIcon={<ViewColumnIcon />}
                    style={{
                        marginTop: 4,
                        marginBottom: 4,
                        borderRadius: 8,
                        height: 36,
                        textTransform: 'capitalize'
                    }}
                    onClick={() => {
                        const effectiveBrandId = selectedBrand || (boards.find(b => Number(b.id) === Number(selectedBoard))?.brandId);
                        const params = new URLSearchParams();
                        if (effectiveBrandId) params.append("brandId", String(effectiveBrandId));
                        if (selectedBoard) params.append("boardId", String(selectedBoard));
                        if (ticket?.id) params.append("ticketId", String(ticket.id));
                        history.push(`/kanban?${params.toString()}`);
                    }}
                >
                    {i18n.t("tags.kanban.goToKanban")}
                </Button>
            )}
        </div>
    );
}
